import type { Block } from './industries';
import routes from './routes.json';
import { LEAD_FORM_ON, withLeadCopy } from './template-lead';

/*
 * Template pages (/templates/<slug>): a free, printable document (a cancellation policy, an estimate) with an explanation around it. One file per page in
 * src/marketing/templates/<slug>.ts (default export: a TemplatePage); published by listing its path in routes.json and the sitemap. The page is made of the
 * same blocks as a trade page plus the `document` block, which is the template itself and is also generated as the PDF download.
 */
export interface TemplatePage {
  slug: string;
  /** `/templates/<slug>` */
  path: string;
  primaryKeyword: string;
  secondaryKeywords: string[];
  /** <= 60 characters, ends with " | Scopie" */
  title: string;
  /** <= 155 characters */
  metaDescription: string;
  h1: string;
  /** 40-60 words, first text under the H1 */
  answerFirst: string;
  /** YYYY-MM-DD: sent as dateModified */
  lastReviewed: string;
  /** schema.org type of the template itself (BreadcrumbList and FAQPage come with it). */
  schemaType: 'CreativeWork' | 'DigitalDocument';
  /** An estimate or quote template that also has the editable Google Sheet (the email form under the PDF button, when the build switch is on). */
  leadSheet?: boolean;
  blocks: Block[];
  /** Mirrors the FAQ block, so the page, its JSON-LD and the tests agree. */
  faq: { q: string; a: string }[];
  /** Shown visibly under the document (required where the template touches legal ground). */
  disclaimer?: string;
  internalLinks: { href: string; anchor: string; status: 'live' | 'planned' }[];
  cta: { heading: string; text: string; button: string; href: string; fine: string };
}

export const templatePages: TemplatePage[] = Object.values(import.meta.glob<{ default: TemplatePage }>('./templates/*.ts', { eager: true })).map((m) => withLeadCopy(m.default, LEAD_FORM_ON));

/** The template pages that are published (listed in routes.json). */
export const liveTemplates = (): TemplatePage[] => templatePages.filter((p) => routes.routes.includes(p.path));
